import {
	API_ENDPOINTS,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	customToast,
	DateFormats,
	Dropdown,
	formatDateFns,
	Icons,
	PageLayout,
	Permissions,
	replaceKeyWithValue,
	route,
	StringHelpers,
	SubTitle,
	useAuthorisationContext,
	useLangContext,
	useStore,
	Variants,
	WidthConstrainedContainer,
} from 'carrier-fe';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ClaimViewData, ClaimPart } from '../../../types/claim';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import CancelClaimModal from '../../../components/CancelClaimModal';
import ClaimEventLog from '../eventLog';
import { DISTRIBUTOR_CLAIM_TYPES } from '../../../util/Constants';

function ClaimView() {
	// Hooks and Contexts
	const { claimId } = useParams();
	const navigate = useNavigate();
	const { crud, fields } = useLangContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	// State Management
	const { store, setValues } = useStore<ClaimViewData>(
		{} as ClaimViewData
	);
	const [cancelModalOpen, setCancelModalOpen] = useState(false);

	// Effects
	useEffect(() => {
		fetchClaimData();
	}, []);

	// Data fetching
	const fetchClaimData = async () => {
		try {
			const { data } = await axios.get<{ data: ClaimViewData }>(
				route(API_ENDPOINTS.REGISTRATION.CLAIM.VIEW, {
					claimId: String(claimId),
				})
			);
			setValues(data.data);
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || 'An error occurred.',
				variant: Variants.Danger,
			});
		}
	};

	// Actions dropdown
	const getActions = (): DropdownItemProps[] => {
		if (!store || !store.permissions) return [];

		let actions: DropdownItemProps[] = [];

		if (store?.permissions?.cancel) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons?.cancel?.default || 'Cancel'
				),
				onClick: () => setCancelModalOpen(true),
				variant: Variants.Danger,
				icon: Icons.CANCEL,
			});
		}

		return actions;
	};

	// Page title
	const title = useMemo(
		() =>
			StringHelpers.title(
				`${replaceKeyWithValue(
					crud?.pages?.view?.title || 'View :model',
					'model',
					crud?.models?.claim || 'Claim'
				)} ${crud?.sub_titles?.details || 'Details'}`
			),
		[crud]
	);

	// Helper for address data
	const address = store?.customer?.primary_site?.address;
	const contactIndividual = store?.customer?.primary_individual;

	return (
		<PageLayout title={title}>
			<WidthConstrainedContainer>
				<div className="mb-4 d-flex justify-content-between align-items-center">
					<Button
						label={StringHelpers.title(
							crud?.buttons?.back?.default || 'Back'
						)}
						onClick={() => navigate('/claims')}
						variant={Variants.Dark}
						type={ButtonTypes.Outline}
					/>
					{getActions().length > 0 && (
						<Dropdown
							label={StringHelpers.title(
								crud?.buttons?.actions?.default ||
									'Actions'
							)}
							items={getActions()}
							type={ButtonTypes.Solid}
							variant={Variants.Info}
						/>
					)}
				</div>

				{/* CONTACT DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className="mb-3 w-100"
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.contact ||
								'Contact'
							} ${
								crud?.sub_titles?.details ||
								'Details'
							}`}
						/>

						<div className="row">
							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											crud?.models?.contact ||
												'Contact'
										)}
									</strong>
									<div>
										{store?.customer_full_name ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.email ||
												'Email'
										)}
									</strong>
									<div>
										{contactIndividual?.email ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.mobile_phone_number ||
												'Mobile'
										)}
									</strong>
									<div>
										{contactIndividual?.display_mobile_number ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.landline_phone_number ||
												'Landline'
										)}
									</strong>
									<div>
										{contactIndividual?.display_landline_number ||
											'-'}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* COMPANY DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className="mb-3 w-100"
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.company ||
								'Company'
							} ${
								crud?.sub_titles?.details ||
								'Details'
							}`}
						/>

						<div className="row">
							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.company_name ||
												'Company name'
										)}
									</strong>
									<div>
										{store?.customer?.name ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_1 ||
												'Address line 1'
										)}
									</strong>
									<div>
										{address?.address_line_1 ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_2 ||
												'Address line 2'
										)}
									</strong>
									<div>
										{address?.address_line_2 ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_3 ||
												'Address line 3'
										)}
									</strong>
									<div>
										{address?.address_line_3 ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.town_city ||
												'Town / City'
										)}
									</strong>
									<div>
										{address?.town_city || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.state_county ||
												'State / County'
										)}
									</strong>
									<div>
										{address?.state_county ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.postcode_zipcode ||
												'Postcode / Zipcode'
										)}
									</strong>
									<div>
										{address?.postcode_zipcode ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.country_code_iso_3 ||
												'Country'
										)}
									</strong>
									<div>
										{address?.country || '-'}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* CLAIM DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className="mb-3 w-100"
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.claim || 'Claim'
							} ${
								crud?.sub_titles?.details ||
								'Details'
							}`}
						/>

						<div className="row">
							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.created_by ||
												'Created by'
										)}
									</strong>
									<div>
										{store?.created_by_individual?.name ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											crud?.models?.installation ||
												'Installation'
										)}
									</strong>
									<div>
										{store?.toshiba_installation
											? [
												store.toshiba_installation.installer_company_name,
												store.toshiba_installation.product_child_name,
											].filter(Boolean).join(' - ') || '-'
											: '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.serial_number ||
												'Serial number'
										)}
									</strong>
									<div>
										{store?.serial_number ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.product_model ||
												'Product model'
										)}
									</strong>
									<div>
										{store?.product_model ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.installation_date ||
												'Installation date'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.installation_date,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											crud?.sub_titles
												?.distributor ||
												'Distributor'
										)}
									</strong>
									<div>
										{store?.distributor?.name ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.status ||
												'Status'
										)}
									</strong>
									<div>
										{store?.status_display ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.approved_by ||
												'Approved by'
										)}
									</strong>
									<div>
										{store
											?.approved_by_individual
											?.name || '-'}
									</div>
								</div>
								
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.approval_date ||
												'Approval date'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.approval_date,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.declined_by ||
												'Declined by'
										)}
									</strong>
									<div>
										{store
											?.declined_by_individual
											?.name || '-'}
									</div>
								</div>
												
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.declined_date ||
												'Declined date'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.declined_date,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>
							
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.reason ||
												'Reason'
										)}
									</strong>
									<div>
										{store?.declined_reason ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.created_at ||
												'Date of claim'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.created_at,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.date_of_failure ||
												'Date of failure'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.date_of_failure,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								{store?.distributor?.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.POST_REPAIR 
									&& (<div className="d-flex flex-column mb-3">
											<strong>
												{StringHelpers.title(
													fields?.date_of_repair ||
														'Date of repair'
												)}
											</strong>
											<div>
												{formatDateFns(
													store?.date_of_repair,
													DateFormats.DATE
												) || '-'}
											</div>
										</div>
									)
								}

								{store?.distributor?.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.PRE_REPAIR 
									&& (<div className="d-flex flex-column mb-3">
											<strong>
												{StringHelpers.title(
													fields?.proposed_date_of_repair ||
														'proposed date of repair'
												)}
											</strong>
											<div>
												{formatDateFns(
													store?.proposed_date_of_repair,
													DateFormats.DATE
												) || '-'}
											</div>
										</div>
									)
								}

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.quantity_of_parts ||
												'Number of parts'
										)}
									</strong>
									<div>
										{store?.parts_count ?? '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.updated_at ||
												'Last updated'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.updated_at,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* PARTS DETAILS */}
				{store?.parts && store.parts.length > 0 && (
					<Card>
						<CardBody>
							<SubTitle
								className="mb-3 w-100"
								style={{ color: '#464C5E' }}
								title={`${
									crud?.models?.part ||
									'Part'
								} ${
									crud?.sub_titles?.details ||
									'Details'
								}`}
							/>

							{store.parts.map(
								(
									part: ClaimPart,
									index: number
								) => (
									<div
										key={part.id || index}
										className={
											index < store.parts.length - 1
												? 'mb-4 pb-4 border-bottom'
												: ''
										}
									>
										<div className="row">
											<div className="col">
												<div className="d-flex flex-column mb-3">
													<strong>
														{StringHelpers.title(
															fields?.part_number ||
																'Part number'
														)}
													</strong>
													<div>
														{part.part_number ||
															'-'}
													</div>
												</div>
											</div>

											<div className="col">
												<div className="d-flex flex-column mb-3">
													<strong>
														{StringHelpers.title(
															fields?.description ||
																'Description'
														)}
													</strong>
													<div>
														{part.description ||
															'-'}
													</div>
												</div>
											</div>

											{part.replacement_dispatch_note_number && (
												<div className="col">
													<div className="d-flex flex-column mb-3">
														<strong>
															{StringHelpers.title(
																fields?.replacement_dispatch_note_number ||
																	'Replacement despatch note number'
															)}
														</strong>
														<div>
															{part.replacement_dispatch_note_number}
														</div>
													</div>
												</div>
											)}

											<div className="col">
												<div className="d-flex flex-column mb-3">
													<strong>
														{StringHelpers.title(
															fields?.previously_repaired ||
																'Previously replaced'
														)}
													</strong>
													<div>
														{part.previously_repaired !== null
															? part.previously_repaired
																? 'Yes'
																: 'No'
															: '-'}
													</div>
												</div>
											</div>
										</div>
									</div>
								)
							)}
						</CardBody>
					</Card>
				)}

				{hasPermissionAccess(Permissions.TOSHIBA_CLAIM_AUDIT_LOG_INDEX) 
					&& <ClaimEventLog />
				}
			</WidthConstrainedContainer>

			<CancelClaimModal
				modalOpen={cancelModalOpen}
				modalClose={() => setCancelModalOpen(false)}
				claimId={claimId || ''}
				storeData={store}
				onSuccess={fetchClaimData}
			/>
		</PageLayout>
	);
}

export default ClaimView;
